/**
 * Der Social-Bereich im Kunden-Admin — eine Komponente fuer alle Websites.
 *
 * Anders als die Blog-Redaktion, die DGSZM und 2N mit ihren eigenen
 * MUI-Komponenten auf den Hooks gebaut haben, ist das hier die mitgelieferte
 * Default-Oberflaeche (Konzept 4.2): Der Social-Teil waechst mit Phase 4
 * (Kalender, Mediathek, Konten verbinden, automatisches Posten), und jede
 * Erweiterung soll bei allen Kunden mit einem Paket-Update ankommen, nicht mit
 * fuenf Nachbauten.
 *
 * Optik nur ueber CSS-Variablen (--ch-bg, --ch-fg, --ch-muted, --ch-accent,
 * --ch-radius, --ch-font, --ch-border, --ch-warn), alles unter `.ch-root` —
 * kein Reset, keine globalen Selektoren. Welche Reiter erscheinen, sagt die
 * Engine (`modules` aus /site), nicht die Website.
 *
 * Ideen erscheinen als Raster wie in den Apps: Hochformat-Kacheln mit dem
 * Original (offizieller Player, siehe ReelPlayer), darunter die Zahlen, die
 * Idee zum Aufklappen.
 */

import { useState } from "react";

import type { HubIdee, HubIdeeStatus, HubModule, HubVorschlag } from "../core/types.js";
import {
  HubProvider,
  type HubProviderProps,
  useHubSite,
  useIdeen,
  useIdeenEinstellungen,
  useIdeenTelegram,
  useVorschlaege,
} from "./hooks.js";
import { MediathekListe } from "./Mediathek.js";
import { SocialKalender } from "./Kalender.js";
import { SocialKommentare } from "./Kommentare.js";
import { SocialKonten } from "./Konten.js";
import { PlanListe } from "./Planung.js";
import { ReelPlayer, useEinbetten } from "./ReelPlayer.js";
import { STYLES } from "./styles.js";

export interface SocialBereichProps {
  /** Eigene Proxy-Route, Standard `/api/content-hub`. */
  basePath?: string;
  /** Name der angemeldeten Person — steht an jeder Entscheidung. */
  userName?: string;
  className?: string;
  /**
   * Nur fuer Admins ohne Session-Cookie (SPA mit Bearer-Token): Header je
   * Anfrage an die Proxy-Route. Alternativ einmal `<HubProvider>` drumherum.
   */
  headers?: HubProviderProps["headers"];
  credentials?: RequestInit["credentials"];
}

type Reiter = "ideen" | "vorschlaege" | "kalender" | "kommentare" | "konten" | "mediathek" | "plan";

const REITER: { key: Reiter; module: HubModule; label: string }[] = [
  { key: "ideen", module: "ideen", label: "Ideen" },
  { key: "vorschlaege", module: "ideen", label: "Vorschläge" },
  { key: "kalender", module: "social", label: "Kalender" },
  { key: "plan", module: "social", label: "Planung" },
  { key: "mediathek", module: "social", label: "Mediathek" },
  { key: "kommentare", module: "social", label: "Kommentare" },
  { key: "konten", module: "social", label: "Konten" },
];

export function SocialBereich({ basePath, headers, credentials, ...rest }: SocialBereichProps) {
  return (
    <HubProvider basePath={basePath} headers={headers} credentials={credentials}>
      <SocialInhalt {...rest} />
    </HubProvider>
  );
}

function SocialInhalt({ userName = "", className = "" }: Pick<SocialBereichProps, "userName" | "className">) {
  const site = useHubSite();
  const modules = site.data?.modules ?? [];
  const sichtbar = REITER.filter((r) => modules.includes(r.module));
  // Zurueck vom Login bei Meta (?meta=…): gleich bei den Konten, dort steht die Rueckmeldung.
  const [gewaehlt, setGewaehlt] = useState<Reiter | null>(() =>
    typeof window !== "undefined" && new URLSearchParams(window.location.search).has("meta") ? "konten" : null);
  const aktiv = sichtbar.find((r) => r.key === gewaehlt)?.key ?? sichtbar[0]?.key ?? null;

  return (
    <div className={`ch-root ch-social ${className}`.trim()}>
      <style>{STYLES}</style>
      {site.loading && !site.data ? (
        <p className="ch-muted">Lädt …</p>
      ) : site.error ? (
        <p className="ch-hinweis">
          {site.offline
            ? "ThunderContent ist gerade nicht erreichbar. Bitte später noch einmal versuchen."
            : `Konnte nicht geladen werden: ${site.error}`}
        </p>
      ) : sichtbar.length === 0 ? (
        <p className="ch-muted">
          Social ist noch nicht gebucht. Sprechen Sie uns an, wenn Sie Video-Ideen
          oder die Social-Planung nutzen möchten.
        </p>
      ) : (
        <>
          {sichtbar.length > 1 && (
            <nav className="ch-tabs" aria-label="Social">
              {sichtbar.map((r) => (
                <button
                  key={r.key}
                  type="button"
                  className={r.key === aktiv ? "ch-tab ch-tab-aktiv" : "ch-tab"}
                  aria-current={r.key === aktiv ? "page" : undefined}
                  onClick={() => setGewaehlt(r.key)}
                >
                  {r.label}
                </button>
              ))}
            </nav>
          )}
          {aktiv === "ideen" && <IdeenListe userName={userName} />}
          {aktiv === "vorschlaege" && <VorschlaegeListe userName={userName} />}
          {aktiv === "mediathek" && <MediathekListe userName={userName} />}
          {aktiv === "plan" && <PlanListe userName={userName} />}
          {aktiv === "kalender" && <SocialKalender userName={userName} />}
          {aktiv === "konten" && <SocialKonten userName={userName} />}
          {aktiv === "kommentare" && <SocialKommentare userName={userName} />}
        </>
      )}
    </div>
  );
}

// -- Ideen -----------------------------------------------------

const GRUPPEN: { status: HubIdeeStatus; label: string; leer: string }[] = [
  { status: "proposed", label: "Neu", leer: "Gerade keine neuen Ideen — die nächsten kommen zum Monatsanfang." },
  { status: "accepted", label: "Setzen wir um", leer: "Noch nichts vorgemerkt." },
  { status: "done", label: "Umgesetzt", leer: "Noch nichts umgesetzt." },
  { status: "declined", label: "Abgelehnt", leer: "Nichts abgelehnt." },
];

export interface IdeenListeProps {
  basePath?: string;
  userName?: string;
}

/** Die Ideen als Raster — auch allein nutzbar, wenn ein Admin sie in eine eigene Seite einbaut. */
export function IdeenListe({ basePath, userName = "" }: IdeenListeProps) {
  const ideen = useIdeen(basePath);
  const einbetten = useEinbetten();
  const [gruppe, setGruppe] = useState<HubIdeeStatus>("proposed");
  const liste = (ideen.data ?? []).filter((i) => i.status === gruppe);
  const anzahl = (s: HubIdeeStatus) => (ideen.data ?? []).filter((i) => i.status === s).length;

  if (ideen.loading && !ideen.data) return <p className="ch-muted">Lädt …</p>;
  if (ideen.error) return <p className="ch-hinweis">Ideen konnten nicht geladen werden: {ideen.error}</p>;

  return (
    <div className="ch-ideen">
      <div className="ch-kopf">
        <p className="ch-muted ch-intro">
          Videos, die zuletzt deutlich besser liefen als sonst bei ihren Konten — übersetzt für Sie.
          Nachgemacht wird das Format, nicht das Video.
        </p>
        {einbetten.an && (
          <button type="button" className="ch-link ch-klein" onClick={einbetten.widerrufen}>
            Videos nicht mehr laden
          </button>
        )}
      </div>
      <TelegramVerbinden basePath={basePath} userName={userName} />
      <Ausschluesse basePath={basePath} />
      <div className="ch-filter" role="tablist">
        {GRUPPEN.map((g) => (
          <button
            key={g.status}
            type="button"
            role="tab"
            aria-selected={g.status === gruppe}
            className={g.status === gruppe ? "ch-chip ch-chip-aktiv" : "ch-chip"}
            onClick={() => setGruppe(g.status)}
          >
            {g.label} <span className="ch-zahl">{anzahl(g.status)}</span>
          </button>
        ))}
      </div>
      {liste.length === 0 ? (
        <p className="ch-muted">{GRUPPEN.find((g) => g.status === gruppe)?.leer}</p>
      ) : (
        <ul className="ch-raster">
          {liste.map((i) => (
            <IdeeKachel key={i.id} idee={i} userName={userName} decide={ideen.decide} />
          ))}
        </ul>
      )}
    </div>
  );
}

/**
 * Videos, die einem selbst gefallen, per Telegram schicken (ab 0.19.0).
 *
 * Der Link oeffnet den Radar-Bot und verbindet das Telegram-Konto mit dieser
 * Website. Danach wird jedes geteilte Video eine Idee nur fuer sie — und die
 * Sammlung fliesst in die naechsten Vorschlaege ein.
 */
function TelegramVerbinden({ basePath, userName }: { basePath?: string; userName: string }) {
  const tg = useIdeenTelegram(basePath);
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const verbunden = (tg.data ?? []).filter((k) => k.linked);
  const offen = (tg.data ?? []).filter((k) => !k.linked);

  async function los(aktion: () => Promise<void>) {
    setBusy(true);
    setFehler(null);
    try {
      await aktion();
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  // Aeltere Engine ohne die Route: den Kasten nicht zeigen statt einer Fehlermeldung.
  if (tg.error && !tg.data) return null;

  return (
    <div className="ch-telegram">
      <p className="ch-text">
        <strong>Eigene Fundstücke schicken.</strong> Teilen Sie Videos, die Ihnen gefallen, aus Instagram
        oder TikTok an unseren Telegram-Bot — wir machen daraus eine Idee für Sie und merken uns, was Sie
        mögen.
      </p>
      {verbunden.length > 0 && (
        <ul className="ch-telegram-liste">
          {verbunden.map((k) => (
            <li key={k.id}>
              Verbunden: {k.name || "Telegram"}{" "}
              <button type="button" className="ch-link ch-klein" disabled={busy}
                onClick={() => void los(() => tg.trennen(k.id))}>
                trennen
              </button>
            </li>
          ))}
        </ul>
      )}
      {tg.link ? (
        <p className="ch-text">
          <a className="ch-knopf ch-knopf-primaer" href={tg.link} target="_blank" rel="noopener noreferrer">
            In Telegram öffnen
          </a>{" "}
          <span className="ch-muted ch-klein">
            Auf dem Handy antippen oder den Link dorthin schicken: {tg.link} — gilt 7 Tage und einmal.
          </span>
        </p>
      ) : (
        <button type="button" className="ch-knopf" disabled={busy}
          onClick={() => void los(() => tg.verbinden(userName))}>
          {verbunden.length > 0 ? "Weiteres Telegram verbinden" : "Mit Telegram verbinden"}
        </button>
      )}
      {!tg.link && offen.length > 0 && (
        <p className="ch-muted ch-klein">
          {offen.length === 1 ? "Ein Link ist" : `${offen.length} Links sind`} noch nicht eingelöst.{" "}
          <button type="button" className="ch-link ch-klein" disabled={busy}
            onClick={() => void los(async () => { for (const k of offen) await tg.trennen(k.id); })}>
            zurückziehen
          </button>
        </p>
      )}
      {fehler && <p className="ch-hinweis">{fehler}</p>}
    </div>
  );
}

function zahl(n: number | null | undefined, platform: string): string {
  if (n === null || n === undefined) return "—";
  const text = n >= 1_000_000
    ? `${(n / 1_000_000).toLocaleString("de-DE", { maximumFractionDigits: 1 })} Mio.`
    : n.toLocaleString("de-DE");
  // TikTok rundet grosse Zahlen — keine Genauigkeit vortaeuschen.
  return platform === "tiktok" && n >= 10_000 ? `ca. ${text}` : text;
}

function alter(iso: string | null): string {
  if (!iso) return "";
  const tage = Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86_400_000));
  return tage === 0 ? "heute" : tage === 1 ? "vor 1 Tag" : `vor ${tage} Tagen`;
}

const PLATTFORM: Record<string, string> = { instagram: "Instagram", tiktok: "TikTok" };

interface KachelProps {
  idee: HubIdee;
  userName: string;
  decide: (id: number, status: Exclude<HubIdeeStatus, "proposed">, by?: string, postUrl?: string) => Promise<void>;
}

function IdeeKachel({ idee, userName, decide }: KachelProps) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState(false);
  const [linkOffen, setLinkOffen] = useState(false);
  const [link, setLink] = useState("");
  const o = idee.original;
  const reaktionen = [
    o.likes != null ? `${zahl(o.likes, o.platform)} Likes` : null,
    o.comments != null ? `${zahl(o.comments, o.platform)} Kommentare` : null,
    o.shares != null ? `${zahl(o.shares, o.platform)} Shares` : null,
  ].filter(Boolean);

  async function entscheiden(status: Exclude<HubIdeeStatus, "proposed">, postUrl = "") {
    setBusy(true);
    setFehler(null);
    try {
      await decide(idee.id, status, userName, postUrl);
      setLinkOffen(false);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="ch-kachel">
      <ReelPlayer src={o.embed_url} platform={o.platform} titel={idee.title} />

      <p className="ch-zahlen">
        <strong>{zahl(o.views, o.platform)}</strong> Aufrufe
        {o.outlier ? (
          <span className="ch-faktor" title="so oft gesehen wie sonst bei diesem Konto üblich">
            {o.outlier.toLocaleString("de-DE")}×
          </span>
        ) : null}
      </p>
      {reaktionen.length > 0 && <p className="ch-zahlen ch-muted">{reaktionen.join(" · ")}</p>}
      <p className="ch-zahlen ch-muted">
        {PLATTFORM[o.platform] ?? o.platform}
        {o.handle ? ` · @${o.handle}` : ""}
        {o.posted_at ? ` · ${alter(o.posted_at)}` : ""}
      </p>

      <h3 className="ch-titel">{idee.title || "Idee"}</h3>
      {idee.origin === "customer" && (
        <p className="ch-muted ch-klein">
          Von Ihnen geschickt{idee.note ? `: „${idee.note}“` : ""}
        </p>
      )}
      <button type="button" className="ch-link" aria-expanded={offen} onClick={() => setOffen(!offen)}>
        {offen ? "Weniger anzeigen" : "Idee ansehen"}
      </button>

      {offen && (
        <div className="ch-details">
          {idee.idea && <p className="ch-text">{idee.idea}</p>}
          {idee.shotlist.length > 0 && (
            <ol className="ch-drehplan">
              {idee.shotlist.map((s, n) => (
                <li key={n}>
                  {s.bild && <span className="ch-bild">{s.bild}</span>}
                  {s.text && <span className="ch-sprech">„{s.text}“</span>}
                </li>
              ))}
            </ol>
          )}
          {idee.needs.length > 0 && (
            <p className="ch-braucht">
              <span className="ch-muted">Braucht: </span>
              {idee.needs.join(" · ")}
            </p>
          )}
          {idee.notes.map((h, n) => (
            <p key={n} className="ch-hinweis">{h}</p>
          ))}
          <p className="ch-vorlage ch-muted">
            <a href={o.url} target="_blank" rel="noopener noreferrer">Original auf {PLATTFORM[o.platform] ?? o.platform} öffnen</a>
          </p>
        </div>
      )}

      {idee.status === "done" && idee.customer_post_url && (
        <p className="ch-muted ch-klein">
          Umgesetzt: <a href={idee.customer_post_url} target="_blank" rel="noopener noreferrer">eigener Beitrag</a>
          {idee.decided_by ? ` (${idee.decided_by})` : ""}
        </p>
      )}
      {idee.status === "done" && idee.result ? (
        <p className="ch-zahlen">
          Ihr Beitrag: <strong>{zahl(idee.result.views, idee.result.platform)}</strong> Aufrufe
          {idee.result.outlier ? (
            <span className="ch-faktor" title="im Vergleich zu Ihren anderen Videos">
              {idee.result.outlier.toLocaleString("de-DE")}×
            </span>
          ) : null}
          {idee.result.age_days != null && (
            <span className="ch-muted"> · nach {Math.max(1, Math.round(idee.result.age_days))} Tagen</span>
          )}
        </p>
      ) : idee.status === "done" && idee.customer_post_url ? (
        <p className="ch-muted ch-klein">Wir messen Ihren Beitrag 7 und 30 Tage nach dem Umsetzen.</p>
      ) : null}

      {linkOffen ? (
        <form
          className="ch-aktionen"
          onSubmit={(e) => {
            e.preventDefault();
            void entscheiden("done", link.trim());
          }}
        >
          <input
            className="ch-eingabe"
            type="url"
            placeholder="Link zum eigenen Beitrag (optional)"
            value={link}
            onChange={(e) => setLink(e.target.value)}
            pattern="https://.*"
          />
          <button type="submit" className="ch-knopf ch-knopf-primaer" disabled={busy}>Speichern</button>
          <button type="button" className="ch-knopf" onClick={() => setLinkOffen(false)} disabled={busy}>Abbrechen</button>
        </form>
      ) : (
        <div className="ch-aktionen">
          {idee.status === "proposed" && (
            <>
              <button type="button" className="ch-knopf ch-knopf-primaer" disabled={busy}
                onClick={() => void entscheiden("accepted")}>Setzen wir um</button>
              <button type="button" className="ch-knopf" disabled={busy}
                onClick={() => void entscheiden("declined")}>Passt nicht</button>
            </>
          )}
          {idee.status === "accepted" && (
            <>
              <button type="button" className="ch-knopf ch-knopf-primaer" disabled={busy}
                onClick={() => setLinkOffen(true)}>Ist umgesetzt</button>
              <button type="button" className="ch-knopf" disabled={busy}
                onClick={() => void entscheiden("declined")}>Doch nicht</button>
            </>
          )}
          {idee.status === "declined" && (
            <button type="button" className="ch-knopf" disabled={busy}
              onClick={() => void entscheiden("accepted")}>Doch umsetzen</button>
          )}
        </div>
      )}
      {fehler && <p className="ch-hinweis">{fehler}</p>}
    </li>
  );
}

// -- Ausschluesse (ab 0.20.0) ----------------------------------

/**
 * Was in Ideen nicht vorkommen soll, als Freitext. Gilt fuer Radar-Ideen und
 * Vorschlaege gleichermassen — ab der naechsten Idee, nicht rueckwirkend.
 */
function Ausschluesse({ basePath }: { basePath?: string }) {
  const e = useIdeenEinstellungen(basePath);
  const [text, setText] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [meldung, setMeldung] = useState<string | null>(null);
  const gespeichert = e.data?.exclusions ?? "";
  const wert = text ?? gespeichert;

  // Aeltere Engine ohne die Route: den Kasten nicht zeigen statt einer Fehlermeldung.
  if (e.error && !e.data) return null;
  if (!e.data) return null;

  async function speichern() {
    setBusy(true);
    setMeldung(null);
    try {
      await e.speichern(wert.trim());
      setText(null);
      setMeldung("Gespeichert — gilt ab den nächsten Ideen.");
    } catch (err) {
      setMeldung(err instanceof Error ? err.message : String(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ch-ausschluesse">
      <label className="ch-text" htmlFor="ch-ausschluesse">
        <strong>Was wir weglassen sollen.</strong>{" "}
        <span className="ch-muted">Zum Beispiel „nichts mit mehreren Menschen“ oder „keine Tanzvideos“.</span>
      </label>
      <textarea
        id="ch-ausschluesse"
        className="ch-eingabe"
        maxLength={1000}
        value={wert}
        placeholder="Frei lassen, wenn nichts ausgeschlossen ist"
        onChange={(ev) => setText(ev.target.value)}
      />
      <div className="ch-aktionen">
        <button type="button" className="ch-knopf" disabled={busy || wert.trim() === gespeichert.trim()}
          onClick={() => void speichern()}>
          Speichern
        </button>
        {meldung && <span className="ch-muted ch-klein">{meldung}</span>}
      </div>
    </div>
  );
}

// -- Vorschlaege ohne Vorlage (ab 0.20.0) ----------------------

const VORSCHLAG_GRUPPEN: { status: HubIdeeStatus; label: string; leer: string }[] = [
  { status: "proposed", label: "Neu", leer: "Noch keine Vorschläge — mit dem Knopf oben entstehen zehn." },
  { status: "accepted", label: "Setzen wir um", leer: "Noch nichts vorgemerkt." },
  { status: "done", label: "Umgesetzt", leer: "Noch nichts umgesetzt." },
  { status: "declined", label: "Abgelehnt", leer: "Nichts abgelehnt." },
];

export interface VorschlaegeListeProps {
  basePath?: string;
  userName?: string;
}

/**
 * Neue Ideen ohne Vorlage: Auf Knopfdruck entwirft die KI zehn weitere — aus
 * der Nische, dem, was angenommen wurde, und den Ausschluessen —, jeweils mit
 * Sprechtext zum Ablesen.
 */
export function VorschlaegeListe({ basePath, userName = "" }: VorschlaegeListeProps) {
  const v = useVorschlaege(basePath);
  const [gruppe, setGruppe] = useState<HubIdeeStatus>("proposed");
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  if (v.loading && !v.data) return <p className="ch-muted">Lädt …</p>;
  if (!v.data) return <p className="ch-hinweis">Vorschläge konnten nicht geladen werden: {v.error}</p>;

  const stand = v.data;
  const liste = stand.items.filter((i) => i.status === gruppe);
  const anzahl = (s: HubIdeeStatus) => stand.items.filter((i) => i.status === s).length;
  const frei = Math.max(0, stand.limit - stand.used);

  async function anfordern() {
    setBusy(true);
    setFehler(null);
    try {
      await v.anfordern(userName);
      setGruppe("proposed");
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="ch-ideen">
      <p className="ch-muted ch-intro">
        Neue Ideen ohne Vorlage — entworfen aus Ihrer Nische, den Ideen, die Sie angenommen haben, und dem, was
        Sie ausgeschlossen haben. Jede mit Sprechtext zum Ablesen.
      </p>
      <Ausschluesse basePath={basePath} />
      <div className="ch-vorschlaege-kopf">
        <button type="button" className="ch-knopf ch-knopf-primaer"
          disabled={busy || stand.running || Boolean(stand.blocked)} onClick={() => void anfordern()}>
          {stand.running ? "Ideen entstehen …" : `${Math.min(10, frei) || 10} weitere Ideen generieren`}
        </button>
        <span className="ch-muted ch-klein">
          {stand.running
            ? "Dauert etwa eine Minute — die Seite kann offen bleiben."
            : `${stand.used} von ${stand.limit} in diesem Monat`}
        </span>
      </div>
      {!stand.running && stand.blocked && <p className="ch-muted">{stand.blocked}</p>}
      {!stand.running && stand.error && <p className="ch-hinweis">{stand.error}</p>}
      {fehler && <p className="ch-hinweis">{fehler}</p>}

      <div className="ch-filter" role="tablist">
        {VORSCHLAG_GRUPPEN.map((g) => (
          <button key={g.status} type="button" role="tab" aria-selected={g.status === gruppe}
            className={g.status === gruppe ? "ch-chip ch-chip-aktiv" : "ch-chip"} onClick={() => setGruppe(g.status)}>
            {g.label} <span className="ch-zahl">{anzahl(g.status)}</span>
          </button>
        ))}
      </div>
      {liste.length === 0 ? (
        <p className="ch-muted">{VORSCHLAG_GRUPPEN.find((g) => g.status === gruppe)?.leer}</p>
      ) : (
        <ul className="ch-liste-vorschlaege">
          {liste.map((i) => <VorschlagKarte key={i.id} vorschlag={i} userName={userName} decide={v.decide} />)}
        </ul>
      )}
    </div>
  );
}

interface VorschlagKarteProps {
  vorschlag: HubVorschlag;
  userName: string;
  decide: (id: number, status: Exclude<HubIdeeStatus, "proposed">, by?: string, postUrl?: string) => Promise<void>;
}

function VorschlagKarte({ vorschlag: s, userName, decide }: VorschlagKarteProps) {
  const [busy, setBusy] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);
  const [offen, setOffen] = useState(false);
  const [linkOffen, setLinkOffen] = useState(false);
  const [link, setLink] = useState("");

  async function entscheiden(status: Exclude<HubIdeeStatus, "proposed">, postUrl = "") {
    setBusy(true);
    setFehler(null);
    try {
      await decide(s.id, status, userName, postUrl);
      setLinkOffen(false);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <li className="ch-vorschlag">
      {s.format && <span className="ch-format">{s.format}</span>}
      <h3 className="ch-titel">{s.title || "Idee"}</h3>
      {s.idea && <p className="ch-text">{s.idea}</p>}
      {s.hook && (
        <p className="ch-text"><span className="ch-muted">Einstieg: </span>{s.hook}</p>
      )}
      <button type="button" className="ch-link" aria-expanded={offen} onClick={() => setOffen(!offen)}>
        {offen ? "Weniger anzeigen" : "Sprechtext und Drehplan"}
      </button>

      {offen && (
        <div className="ch-details">
          {s.transcript && (
            <>
              <p className="ch-muted ch-klein">Sprechtext</p>
              <p className="ch-transkript">{s.transcript}</p>
            </>
          )}
          {s.shotlist.length > 0 && (
            <ol className="ch-drehplan">
              {s.shotlist.map((x, n) => (
                <li key={n}>
                  {x.bild && <span className="ch-bild">{x.bild}</span>}
                  {x.text && <span className="ch-sprech">„{x.text}“</span>}
                </li>
              ))}
            </ol>
          )}
          {s.needs.length > 0 && (
            <p className="ch-braucht"><span className="ch-muted">Braucht: </span>{s.needs.join(" · ")}</p>
          )}
          {s.notes.map((h, n) => <p key={n} className="ch-hinweis">{h}</p>)}
          {s.why && <p className="ch-muted ch-klein">Baut auf: {s.why}</p>}
        </div>
      )}

      {s.status === "done" && s.customer_post_url && (
        <p className="ch-muted ch-klein">
          Umgesetzt: <a href={s.customer_post_url} target="_blank" rel="noopener noreferrer">eigener Beitrag</a>
          {s.decided_by ? ` (${s.decided_by})` : ""}
        </p>
      )}

      {linkOffen ? (
        <form className="ch-aktionen" onSubmit={(e) => { e.preventDefault(); void entscheiden("done", link.trim()); }}>
          <input className="ch-eingabe" type="url" placeholder="Link zum eigenen Beitrag (optional)" value={link}
            onChange={(e) => setLink(e.target.value)} pattern="https://.*" />
          <button type="submit" className="ch-knopf ch-knopf-primaer" disabled={busy}>Speichern</button>
          <button type="button" className="ch-knopf" onClick={() => setLinkOffen(false)} disabled={busy}>Abbrechen</button>
        </form>
      ) : (
        <div className="ch-aktionen">
          {s.status === "proposed" && (
            <>
              <button type="button" className="ch-knopf ch-knopf-primaer" disabled={busy}
                onClick={() => void entscheiden("accepted")}>Setzen wir um</button>
              <button type="button" className="ch-knopf" disabled={busy}
                onClick={() => void entscheiden("declined")}>Passt nicht</button>
            </>
          )}
          {s.status === "accepted" && (
            <>
              <button type="button" className="ch-knopf ch-knopf-primaer" disabled={busy}
                onClick={() => setLinkOffen(true)}>Ist umgesetzt</button>
              <button type="button" className="ch-knopf" disabled={busy}
                onClick={() => void entscheiden("declined")}>Doch nicht</button>
            </>
          )}
          {s.status === "declined" && (
            <button type="button" className="ch-knopf" disabled={busy}
              onClick={() => void entscheiden("accepted")}>Doch umsetzen</button>
          )}
        </div>
      )}
      {fehler && <p className="ch-hinweis">{fehler}</p>}
    </li>
  );
}
